import { useState } from 'react';
import { Mic, Square, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { useUpdateInteractionStatus } from 'hooks/interactionStatus';
import { usePausedConsultationNavigation } from 'hooks/usePausedConsultationNavigation';
import type { PausedConsultation } from 'types/pausedConsultation';
import { DiscardConsultationDialog } from './DiscardConsultationDialog';
import {
  ActionsRow,
  DiscardButton,
  EndAndTranscribeButton,
  ResumeButton,
  SecondaryActions,
} from './PausedConsultationActions.styles';

interface PausedConsultationActionsProps {
  consultation: PausedConsultation;
}

export const PausedConsultationActions = ({ consultation }: PausedConsultationActionsProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const { mutate: updateInteractionStatus, isPending: isDiscarding } = useUpdateInteractionStatus();
  const { resume, endAndTranscribe } = usePausedConsultationNavigation();

  const handleConfirmDiscard = () => {
    updateInteractionStatus(
      { interactionId: consultation.interactionId, status: 'discarded' },
      {
        onSuccess: () => {
          trackEvent(ANALYTICS_EVENTS.PATIENT_DISCARD_RECORDING);
          setIsDiscardDialogOpen(false);
        },
      }
    );
  };

  return (
    <>
      <ActionsRow>
        <DiscardButton onClick={() => setIsDiscardDialogOpen(true)}>
          <Trash2 className="size-3" />
          {t('recording.pausedConsultation.discard')}
        </DiscardButton>
        <SecondaryActions>
          <EndAndTranscribeButton onClick={() => endAndTranscribe(consultation)}>
            <Square className="size-3" />
            {t(
              consultation.withoutRecording
                ? 'recording.pausedConsultation.endWithoutRecording'
                : 'recording.pausedConsultation.endAndTranscribe'
            )}
          </EndAndTranscribeButton>
          <ResumeButton onClick={() => resume(consultation)}>
            {!consultation.withoutRecording && <Mic className="size-3" />}
            {t('recording.pausedConsultation.resume')}
          </ResumeButton>
        </SecondaryActions>
      </ActionsRow>
      {isDiscardDialogOpen && (
        <DiscardConsultationDialog
          isDiscarding={isDiscarding}
          onConfirm={handleConfirmDiscard}
          onClose={() => setIsDiscardDialogOpen(false)}
        />
      )}
    </>
  );
};
